import { BrandingConfig, PageConfig } from "@apis:ui_branding/ui_branding";

/**
 * Field-level merge of a page config.
 * Bot values take precedence over org values; unset bot fields fall back to the org.
 */
export const mergePageConfigs = (
  botPage: PageConfig | null | undefined,
  orgPage: PageConfig | null | undefined,
): PageConfig | null => {
  if (!botPage) return orgPage ?? null;
  if (!orgPage) return botPage;

  const keys: (keyof PageConfig)[] = [
    "iconKey",
    "customIconSvg",
    "tooltip",
    "title",
    "description",
  ];

  const merged: PageConfig = {} as PageConfig;
  for (const key of keys) {
    merged[key] = botPage[key] ?? orgPage[key];
  }

  return merged;
};

/**
 * Field-level merge of two branding configs.
 * Bot branding takes precedence over org branding; unset bot fields fall back to the org.
 */
export const mergeBrandingConfigs = (
  botConfig: BrandingConfig | null | undefined,
  orgConfig: BrandingConfig | null | undefined,
): BrandingConfig | null => {
  if (!botConfig) return orgConfig ?? null;
  if (!orgConfig) return botConfig;

  const keys: (keyof Omit<BrandingConfig, "pages">)[] = [
    "logoBase64",
    "primaryColor",
    "secondaryColor",
    "sidebarColor",
    "backgroundColor",
    "browserTitle",
    "faviconUrl",
    "faviconBase64",
  ];

  // Copy orgConfig values first, then overwrite with botConfig values if present
  const merged: BrandingConfig = { ...orgConfig };
  for (const key of keys) {
    const value = botConfig[key];
    if (value != null) {
      merged[key] = value;
    }
  }

  // Merge the nested `pages` map per page key
  const pageKeys = new Set<string>([
    ...Object.keys(orgConfig.pages ?? {}),
    ...Object.keys(botConfig.pages ?? {}),
  ]);

  const mergedPages: BrandingConfig["pages"] = {};
  for (const key of pageKeys) {
    const mergedPage = mergePageConfigs(
      botConfig.pages?.[key],
      orgConfig.pages?.[key],
    );

    if (mergedPage) {
      mergedPages[key] = mergedPage;
    }
  }

  merged.pages = Object.keys(mergedPages).length > 0 ? mergedPages : undefined;

  return merged;
};
